/**
 * Frontend permissions catalogue — iter182
 *
 * Mirrors backend/permissions.py. Kept intentionally small: the source
 * of truth remains the backend (fetched by AdminsTab). This file is
 * only used for the runtime permission-check hook so the UI can hide
 * or disable actions the user isn't allowed to perform.
 */

// Same 25 keys as the backend catalogue, grouped for display.
export const PERM_GROUPS = [
  {
    id: "users",
    ar: "المستخدمون",
    en: "Users",
    perms: [
      { key: "users.view",     ar: "عرض المستخدمين",           en: "View users" },
      { key: "users.manage",   ar: "إنشاء/تعديل المستخدمين",   en: "Create / edit" },
      { key: "users.suspend",  ar: "إيقاف/تفعيل",              en: "Suspend / activate" },
      { key: "users.delete",   ar: "حذف المستخدمين",           en: "Delete users" },
      { key: "users.verify",   ar: "توثيق المستخدمين",         en: "Verify users" },
      { key: "users.business_verify", ar: "توثيق الأعمال",     en: "Business verification" },
    ],
  },
  {
    id: "listings",
    ar: "الإعلانات",
    en: "Listings",
    perms: [
      { key: "listings.vehicles.manage",   ar: "إدارة المركبات", en: "Manage vehicles" },
      { key: "listings.activities.manage", ar: "إدارة الأنشطة",   en: "Manage activities" },
      { key: "listings.reports.manage",    ar: "إدارة البلاغات",  en: "Manage reports" },
      { key: "listings.disputes.resolve",  ar: "حل النزاعات",     en: "Resolve disputes" },
      { key: "listings.imports.manage",    ar: "إدارة استيراد الإعلانات", en: "Manage listing imports" },
      { key: "listings.imports.approve",   ar: "اعتماد تفويضات الاستيراد", en: "Approve import authorizations" },
    ],
  },
  {
    id: "subscriptions",
    ar: "الاشتراكات",
    en: "Subscriptions",
    perms: [
      { key: "subscriptions.plans.manage",          ar: "إدارة الباقات",         en: "Manage plans" },
      { key: "subscriptions.grants.manage",         ar: "منح/سحب الاشتراكات",   en: "Grant / revoke" },
      { key: "subscriptions.private_offers.manage", ar: "العروض الخاصة",         en: "Private offers" },
      { key: "subscriptions.campaigns.manage",      ar: "الحملات والعروض",       en: "Campaigns" },
    ],
  },
  {
    id: "finance",
    ar: "المالية",
    en: "Finance",
    perms: [
      { key: "finance.payments.view",     ar: "عرض المدفوعات",     en: "View payments" },
      { key: "finance.transfers.approve", ar: "اعتماد التحويلات", en: "Approve transfers" },
      { key: "finance.refunds.manage",    ar: "الاسترداد",          en: "Refunds" },
      { key: "finance.reports.view",      ar: "التقارير المالية", en: "Financial reports" },
    ],
  },
  {
    id: "marketing",
    ar: "التسويق",
    en: "Marketing",
    perms: [
      { key: "marketing.notifications.send", ar: "إرسال الإشعارات",        en: "Send notifications" },
      { key: "marketing.banners.manage",     ar: "إدارة البنرات",           en: "Manage banners" },
      { key: "marketing.content.manage",     ar: "المحتوى الترويجي",       en: "Promotional content" },
    ],
  },
  {
    id: "admin",
    ar: "الإدارة",
    en: "Administration",
    perms: [
      { key: "admin.accounts.manage", ar: "حسابات المسؤولين",        en: "Admin accounts" },
      { key: "admin.roles.manage",    ar: "الأدوار والصلاحيات",       en: "Roles & permissions" },
      { key: "admin.settings.manage", ar: "إعدادات المنصة",           en: "Platform settings" },
      { key: "admin.audit.view",      ar: "سجل التدقيق",              en: "Audit logs" },
      { key: "admin.reports.view",    ar: "تقارير النظام",             en: "System reports" },
      { key: "admin.analytics.view",  ar: "التحليلات",                 en: "Analytics" },
    ],
  },
  // iter225 — every remaining admin dashboard tab that did not yet map
  // to a specific permission. Each key gates ONE section; ticking it
  // grants access to that section only. The list mirrors permissions.py
  // one-to-one — appending a new row here (and in the backend catalogue)
  // makes it selectable immediately with no other UI change. Any
  // future dashboard section MUST add its permission key here so it
  // appears in the picker automatically.
  {
    id: "sections",
    ar: "أقسام لوحة التحكم",
    en: "Dashboard sections",
    perms: [
      { key: "sections.archive.individuals", ar: "أرشيف الأفراد",        en: "Individuals archive" },
      { key: "sections.archive.businesses",  ar: "أرشيف الأنشطة",       en: "Businesses archive" },
      { key: "sections.cartal_channel",      ar: "قناة كارتال",         en: "CARTAL channel" },
      { key: "sections.messages",            ar: "الرسائل الإدارية",    en: "Admin messages" },
      { key: "sections.partners",            ar: "الشركاء",             en: "Partners" },
      { key: "sections.other_services",      ar: "الخدمات الأخرى",      en: "Other services" },
      { key: "sections.faq",                 ar: "الأسئلة الشائعة",     en: "FAQ" },
      { key: "sections.makes_models",        ar: "الشركات المصنّعة والموديلات", en: "Makes & models" },
      { key: "sections.geo_spread",          ar: "الانتشار الجغرافي",   en: "Geo spread" },
      { key: "sections.ad_campaigns",        ar: "الحملات الإعلانية",   en: "Ad campaigns" },
      { key: "sections.verification_audit",  ar: "سجلّ تدقيق التوثيق", en: "Verification audit" },
      { key: "sections.backups",             ar: "النسخ الاحتياطية",   en: "Backups" },
    ],
  },
];

export const ALL_PERM_KEYS = PERM_GROUPS.flatMap((g) => g.perms.map((p) => p.key));

// Backwards-compatibility with the legacy 8-key set.
export const LEGACY_EXPANSION = {
  "users.manage":     ["users.view", "users.manage", "users.suspend", "users.delete", "users.verify"],
  "vehicles.manage":  ["listings.vehicles.manage", "listings.activities.manage"],
  "disputes.manage":  ["listings.disputes.resolve", "listings.reports.manage"],
  "reports.review":   ["listings.reports.manage"],
  "broadcasts.send":  ["marketing.notifications.send"],
  "banners.manage":   ["marketing.banners.manage", "marketing.content.manage"],
  "settings.edit":    ["admin.settings.manage", "admin.reports.view", "admin.audit.view"],
  "payouts.approve":  ["finance.payments.view", "finance.transfers.approve",
                        "finance.refunds.manage", "finance.reports.view"],
};

export function expandPermissions(perms) {
  if (!perms || !perms.length) return new Set();
  const out = new Set();
  for (const p of perms) {
    if (!p) continue;
    const bundle = LEGACY_EXPANSION[p];
    if (bundle) bundle.forEach((x) => out.add(x));
    else out.add(p);
  }
  return out;
}

/**
 * Runtime permission check. super_admin bypasses every gate.
 * `user` is the decoded auth user (with `role` + `permissions`).
 * `perm` can be a single string OR an array — array means "user needs
 * ANY of these".
 */
export function hasPerm(user, perm) {
  if (!user) return false;
  if (user.role === "super_admin") return true;
  if (user.role !== "admin") return false;
  const set = expandPermissions(user.permissions || []);
  if (Array.isArray(perm)) return perm.some((p) => set.has(p));
  return set.has(perm);
}

// React hook wrapper — colocated with the catalogue so callers only
// need to import from one file.
import { useAuth } from "./auth";
export function usePermissions() {
  const { user } = useAuth();
  return {
    user,
    has: (perm) => hasPerm(user, perm),
    isSuperAdmin: user?.role === "super_admin",
    isAdmin: user?.role === "admin" || user?.role === "super_admin",
  };
}
